import { useMemo, VFC } from 'react';

import ReactSelect from 'dashboard/components/ReactSelect';
import ParamLabel from 'dashboard/components/WorkflowControlPanel/ParamLabel';
import { TYPE_TO_JSON_SCHEMA } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsEvaluateCodeV2';
import { jsonSchemaToEvalCodeReturnType } from 'dashboard/lib/workflows/util';
import { Trigger } from 'dashboard/models/trigger';
import {
  ActionFor,
  StaticWorkflowAction,
  WorkflowActionEvaluateCodeV1,
  WorkflowActionEvaluateCodeV2,
  WorkflowStepActionKey,
} from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import { FieldSchemaType } from 'lib/fields';
import useIsPersonerd from 'lib/hooks/useIsPersonerd';
import { ApiModel } from 'lib/interfaces/api';

type ActionInMigration = {
  [P in StaticWorkflowAction['key']]: ActionFor<P>['version'][];
};

interface ActionVersionProps {
  action: StaticWorkflowAction;
  editable: boolean;
  trigger?: Trigger;
  updateAction: (action: StaticWorkflowAction) => void;
}

/*
 * Component for Personerd to migrate workflow step action's version
 * This component shows up version selector if given action is eligible to migrate for only Personerd
 * To add new support of version migration for action step, update `versionsMap` and `migrateAction`
 */
const ActionVersion: VFC<ActionVersionProps> = ({
  action: currentAction,
  editable,
  trigger,
  updateAction,
}) => {
  const isPersonerd = useIsPersonerd();
  const versionsMap: Partial<ActionInMigration> = useMemo(
    () => ({
      [WorkflowStepActionKey.EvaluateCode]: [1, 2],
    }),
    [],
  );

  const possibleVersions: number[] = useMemo(() => {
    return versionsMap[currentAction?.key] ?? [];
  }, [currentAction, versionsMap]);

  const migrateAction = (newVersion: string) => {
    switch (currentAction?.key) {
      case WorkflowStepActionKey.EvaluateCode: {
        if (currentAction.version === 2 && newVersion === '1') {
          const typedAction = currentAction as WorkflowActionEvaluateCodeV2;
          const returnType = jsonSchemaToEvalCodeReturnType(
            typedAction.params['return-schema']['config']['json-schema'],
          );
          const newAction: WorkflowActionEvaluateCodeV1 = {
            key: typedAction.key,
            version: 1,
            params: {
              'return-type': returnType === 'object' ? 'void' : returnType,
              'serialized-code': typedAction.params['serialized-code'],
              trigger: 'trigger',
              ...(trigger && [ApiModel.TriggerApi, ApiModel.TriggerCase].includes(trigger.type)
                ? {
                    'trigger-params': 'trigger_params',
                  }
                : {}),
              ...(typedAction.params['retry-intervals']
                ? {
                    'retry-intervals': typedAction.params['retry-intervals'],
                  }
                : {}),
            },
          };
          updateAction(newAction);
        } else if (currentAction.version === 1 && newVersion === '2') {
          const typedAction = currentAction as WorkflowActionEvaluateCodeV1;
          const newAction: WorkflowActionEvaluateCodeV2 = {
            key: typedAction.key,
            version: 2,
            params: {
              'input-args': {},
              'return-schema': {
                type: FieldSchemaType.Json,
                key: 'result',
                label: 'Result',
                config: {
                  'json-schema': TYPE_TO_JSON_SCHEMA[typedAction.params['return-type']],
                },
              },
              'serialized-code': typedAction.params['serialized-code'],
              ...(typedAction.params['retry-intervals']
                ? {
                    'retry-intervals': typedAction.params['retry-intervals'],
                  }
                : {}),
              timeout: null,
            },
          };
          updateAction(newAction);
        }
      }
    }
  };

  return (
    <>
      {currentAction && isPersonerd && possibleVersions.length > 1 && (
        <>
          <Spacer height={2} />
          <ParamLabel
            htmlFor='action-version__select-input'
            labelText='Version'
            helpText='Migrate version. This dropdown only shows for Personerd'
          />
          <ReactSelect
            disabled={!editable}
            id='action-version__select'
            options={possibleVersions.map((number) => ({
              label: `v${number}`,
              value: number.toString(),
            }))}
            value={currentAction.version.toString()}
            onChange={migrateAction}
          />
        </>
      )}
    </>
  );
};

export default ActionVersion;
